import { MAX_USABLE_ACCURACY_METERS, type Coords } from '@rabbitfuel/shared';
import { formatMeters } from '../components/GeofencePicker.tsx';

/** Ab hier ist der Fix gut genug -- laenger auf GPS zu warten bringt nichts. */
const GOOD_ACCURACY_METERS = 50;
/** So lange geben wir dem GPS Zeit, bevor der beste bisherige Fix gilt. */
const GEO_WAIT_MS = 8000;

export interface LocateResult {
  location: Coords | null;
  /** Lesbarer Grund, wenn es keinen brauchbaren Standort gab. */
  error: string | null;
}

/**
 * Standort abfragen -- mit Geduld. Gebraucht fuer Kamera-Anfragen in einem
 * Event mit Bereich und fuer Botschaften bei „Nur vor Ort".
 *
 * `getCurrentPosition` antwortet oft sofort mit einer groben Schaetzung aus
 * WLAN oder IP, waehrend das GPS noch sucht; die kann hunderte Kilometer
 * danebenliegen. Deshalb hoeren wir ein paar Sekunden zu und nehmen den
 * besten Fix: sobald einer genau genug ist, sind wir fertig, sonst gewinnt
 * nach Ablauf der beste bis dahin. `maximumAge: 0` verhindert zusaetzlich,
 * dass ein alter WLAN-Fix aus dem Cache zurueckkommt.
 */
export function bestLocation(): Promise<LocateResult> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ location: null, error: 'Dieser Browser gibt keinen Standort heraus.' });
      return;
    }
    let watchId: number | null = null;
    let timer: number | null = null;
    let best: Coords | null = null;
    let error: string | null = null;
    let done = false;

    const stop = () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (timer !== null) window.clearTimeout(timer);
      watchId = null;
      timer = null;
    };
    const finish = (value: Coords | null) => {
      if (done) return;
      done = true;
      stop();
      if (value && (value.accuracy ?? 0) > MAX_USABLE_ACCURACY_METERS) {
        error =
          `Standort nur auf ${formatMeters(Math.round(value.accuracy!))} genau — das reicht für ` +
          'den Event-Bereich nicht. Schalte GPS ein und versuche es im Freien erneut.';
      }
      resolve({ location: value, error });
    };

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const fix: Coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        };
        if (!best || (fix.accuracy ?? Infinity) < (best.accuracy ?? Infinity)) best = fix;
        // Genau genug -- laenger warten bringt nichts mehr.
        if ((fix.accuracy ?? Infinity) <= GOOD_ACCURACY_METERS) finish(fix);
      },
      (err) => {
        error =
          err.code === err.PERMISSION_DENIED ? 'Standort nicht freigegeben.' : 'Standort konnte nicht bestimmt werden.';
        // Ein frueher Fehler heisst nicht, dass gar nichts kam.
        finish(best);
      },
      { enableHighAccuracy: true, timeout: GEO_WAIT_MS, maximumAge: 0 },
    );
    timer = window.setTimeout(() => finish(best), GEO_WAIT_MS);
    if (done) stop();
  });
}
